import React, { FC, useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Skeleton } from "@mui/material";

import { useAuth } from "@hooks/auth";

import { createFavorites, deleteFavorites } from "@store/actions/favorites/favorites";
import { useAppDispatchUnwrap, useAppSelector } from "@store/hooks";
import { getFavoritesState } from "@store/selectors/getFavorites";

import { ICatalogue } from "@models/models";
import { ERoutes } from "@utils/constants";
import { makePath } from "@utils/makePath";
import { refusedWhileViewing } from "@utils/viewAs";
import styled, { keyframes } from "styled-components";

// 700ms fade-in shared by all cover images on this page — runs every time the
// component mounts so the fade is visible even when the image is already in
// browser cache (vs the onLoad-based approach which is instant for warm cache).
const coverFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// -- Styled components --

const AlbumCard = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(235, 194, 142, 0.3);
  border-radius: 12px;
  overflow: hidden;
  /* Fill the grid cell (cells stretch to the row's tallest tile) and lay out as
     a column so the artist + track count can pin to the bottom — keeping them
     aligned across tiles even when one title is 1 line and another is 2. */
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const AlbumCoverImg = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
  cursor: pointer;
  transition: transform 0.3s ease, filter 0.3s ease;
  animation: ${coverFadeIn} 700ms ease both;

  &:hover {
    transform: scale(1.03);
    filter: brightness(1.1);
  }
`;

const AlbumInfo = styled.div`
  padding: 14px;
  /* Fill the card's remaining height so the artist + meta can be pinned to the
     bottom (margin-top:auto on the artist row), keeping them aligned across a
     row whose tiles have different title-line counts. */
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TitleRow = styled.div`
  margin-bottom: 4px;
  /* Show the full title across up to two lines, but DON'T reserve a fixed
     two-line height — let the title take its natural 1 or 2 lines. The grid
     stretches cards to the tallest in their row, so a row whose titles are all
     one line collapses to a shorter height, while a row containing a two-line
     title stays aligned. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AlbumCardTitle = styled.span`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.25rem;
  font-weight: 500;
  color: #fbf4ec;
  line-height: 1.1;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #ffdca8;
  }
`;

const ArtistRow = styled.div`
  /* Push the artist + the track-count meta below it to the bottom of the card,
     so they bottom-align across tiles in the same row regardless of how many
     lines each title takes. */
  margin-top: auto;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AlbumArtist = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 0.8125rem;
  color: #efd0b8;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #f7ede6;
  }
`;

const AlbumMeta = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.8125rem;
  color: rgba(251, 244, 236, 0.5);
`;

const HeartBtn = styled.button<{ $active: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  z-index: 2;

  svg {
    width: 18px;
    height: 18px;
    stroke: ${({ $active }) => ($active ? "#fff" : "rgba(251, 244, 236, 0.7)")};
    fill: ${({ $active }) => ($active ? "#fff" : "none")};
    stroke-width: 1.5;
    transition: all 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.6);
    svg {
      stroke: #fff;
    }
  }
`;

const CoverWrap = styled.div`
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  &:hover ${HeartBtn} {
    opacity: 1;
  }
`;

// -- Types --

interface IAlbumList {
  isCollapsed?: boolean;
  album: ICatalogue;
}

export enum EAlbumList {
  IsFavorite = "isFavorite",
  OpenModal = "openModal",
  ActiveStep = "activeStep",
  IsAlbumCoverLoading = "isAlbumCoverLoading",
  ShowMore = "showMore",
  IsCopyright = "isCopyright",
  OpenFreeUseModal = "openFreeUseModal",
  OpenLicenseModal = "openLicenseModal",
}

export type AlbumListAction =
  | {
      type:
        | EAlbumList.ShowMore
        | EAlbumList.IsAlbumCoverLoading
        | EAlbumList.OpenModal
        | EAlbumList.IsFavorite
        | EAlbumList.IsCopyright
        | EAlbumList.OpenFreeUseModal
        | EAlbumList.OpenLicenseModal;
      payload: boolean;
    }
  | { type: EAlbumList.ActiveStep; payload: number };

export type AlbumListState = {
  isFavorite: boolean;
  openModal: boolean;
  activeStep: number;
  isAlbumCoverLoading: boolean;
  showMore: boolean;
  isCopyright: boolean;
  openFreeUseModal: boolean;
  openLicenseModal: boolean;
};

export const albumListReducer = (state: AlbumListState, action: AlbumListAction) => {
  const { type, payload } = action;
  switch (type) {
    case EAlbumList.IsFavorite:
      return { ...state, isFavorite: payload };
    case EAlbumList.OpenModal:
      return { ...state, openModal: payload };
    case EAlbumList.ActiveStep:
      return { ...state, activeStep: payload };
    case EAlbumList.IsAlbumCoverLoading:
      return { ...state, isAlbumCoverLoading: payload };
    case EAlbumList.ShowMore:
      return { ...state, showMore: payload };
    case EAlbumList.IsCopyright:
      return { ...state, isCopyright: payload };
    case EAlbumList.OpenFreeUseModal:
      return { ...state, openFreeUseModal: payload };
    case EAlbumList.OpenLicenseModal:
      return { ...state, openLicenseModal: payload };
    default:
      return state;
  }
};

const AlbumList: FC<IAlbumList> = ({ isCollapsed: _isCollapsed = false, album }) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const dispatch = useAppDispatchUnwrap();
  const favorites = useAppSelector(getFavoritesState);
  const reduxFavorite = favorites.some((f: { albumId?: string }) => f.albumId === album._id);
  // Optimistic override so the heart flips on tap; cleared once Redux catches
  // up (or reflects an external change), at which point the derived value wins.
  const [optimisticFavorite, setOptimisticFavorite] = useState<boolean | null>(null);
  const isFavorite = optimisticFavorite ?? reduxFavorite;
  useEffect(() => { setOptimisticFavorite(null); }, [reduxFavorite]);

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLoggedIn) return;
    // Optimistic control: it flips before the server answers, so a view has
    // to be turned away here rather than left to show a change that was refused.
    if (refusedWhileViewing()) return;
    const willFavorite = !isFavorite;
    setOptimisticFavorite(willFavorite);
    void dispatch(willFavorite
      ? createFavorites({ albumId: album._id })
      : deleteFavorites({ albumId: album._id }));
  };

  const [state, dispatchReducer] = useReducer(albumListReducer, {
    isFavorite: false,
    isAlbumCoverLoading: true,
    activeStep: 1,
    openModal: false,
    showMore: false,
    isCopyright: false,
    openFreeUseModal: false,
    openLicenseModal: false,
  });

  const albumPath = makePath(ERoutes.Album, album.slug);

  const handleGoToAlbum = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) { e.preventDefault(); window.open(albumPath, "_blank"); return; }
    navigate(albumPath);
  };

  const preventAutoScroll = (e: React.MouseEvent) => { if (e.button === 1) e.preventDefault(); };
  const middleClickAlbum = (e: React.MouseEvent) => {
    if (e.button === 1) { e.preventDefault(); window.open(albumPath, "_blank"); }
  };

  const composers = album.summary?.composer
    ?.map((c) => (c && typeof c === "object" ? (c as { name: string }).name : c)) || [];
  // Albums whose tracks span more than one artist show "Various Artists" rather
  // than a comma-joined list (which ellipsised awkwardly on the tile).
  const composerNames = composers.length > 1 ? "Various Artists" : composers.join(", ");

  const handleNavigateComposer = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (composers[0]) {
      const path = `${ERoutes.Catalog}?q=${encodeURIComponent(composers[0] as string)}&t=${Date.now()}`;
      if (e.ctrlKey || e.metaKey) { e.preventDefault(); window.open(path, "_blank"); return; }
      navigate(path);
    }
  };

  return (
    <AlbumCard>
      <CoverWrap>
        {state.isAlbumCoverLoading && (
          <Skeleton
            sx={{
              width: "100%",
              aspectRatio: "1",
              borderRadius: 0,
            }}
            variant="rectangular"
          />
        )}
        <AlbumCoverImg
          key={album.cardCoverUrl || album.mediumCoverUrl || album.largeCoverUrl || album.smallCoverUrl}
          src={album.cardCoverUrl || album.mediumCoverUrl || album.largeCoverUrl || album.smallCoverUrl}
          alt={album.name}
          onClick={handleGoToAlbum}
          onMouseDown={preventAutoScroll}
          onMouseUp={middleClickAlbum}
          onLoad={() =>
            dispatchReducer({
              type: EAlbumList.IsAlbumCoverLoading,
              payload: false,
            })
          }
        />
        {isLoggedIn && (
          <HeartBtn $active={isFavorite} onClick={handleToggleFavorite}>
            <svg viewBox="0 0 24 24">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </HeartBtn>
        )}
      </CoverWrap>
      <AlbumInfo>
        <TitleRow><AlbumCardTitle onClick={handleGoToAlbum} onMouseDown={preventAutoScroll} onMouseUp={middleClickAlbum}>{album.name}</AlbumCardTitle></TitleRow>
        <ArtistRow><AlbumArtist onClick={handleNavigateComposer} onMouseUp={(e) => { e.stopPropagation(); if (composers[0]) { const p = `${ERoutes.Catalog}?q=${encodeURIComponent(composers[0] as string)}&t=${Date.now()}`; if (e.button === 1) { e.preventDefault(); window.open(p, "_blank"); } } }}>{composerNames}</AlbumArtist></ArtistRow>
        <AlbumMeta>{album.numTracks} tracks</AlbumMeta>
      </AlbumInfo>
    </AlbumCard>
  );
};

export default AlbumList;
